import * as React from "react";
import { api, idempotencyKey, postJson } from "./api";
import { AppLink } from "./router";
import { useRoomSession } from "./room-session";

export interface FragmentView {
  id: string;
  parentFragmentId?: string | null;
  rootFragmentId?: string;
  visibility: "public" | "private";
  masked?: boolean;
  label?: string;
  state?: string;
  sourceKind?: string;
  note?: string | null;
  track?: {
    spotifyId: string;
    uri: string;
    title: string;
    artist: string;
    album: string | null;
    artworkUrl: string | null;
    durationMs: number;
    spotifyUrl?: string;
  };
  creator?: { handle: string; displayName: string; avatarUrl: string | null };
  recipient?: { handle: string; displayName: string } | null;
  addedToLibrary?: boolean;
  propagationCount: number;
  featuredRank?: number;
  depth?: number;
  createdAt: string;
}

function ActionPanel({ fragment, initialMode, onClose, onChanged }: {
  fragment: FragmentView;
  initialMode: "profile" | "room";
  onClose: () => void;
  onChanged: () => void;
}) {
  const [mode, setMode] = React.useState<"profile" | "room">(initialMode);
  const [handle, setHandle] = React.useState("");
  const [visibility, setVisibility] = React.useState<"public" | "private">("public");
  const [note, setNote] = React.useState("");
  const [rooms, setRooms] = React.useState<Array<{ id: string; alias: string; title: string }>>([]);
  const [roomId, setRoomId] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (mode !== "room") return;
    void api<{ rooms: Array<{ id: string; alias: string; title: string }> }>("/api/me/rooms")
      .then((payload) => {
        setRooms(payload.rooms);
        setRoomId(payload.rooms[0]?.id || "");
      })
      .catch((cause) => setError(cause?.message || "Could not load your rooms"));
  }, [mode]);

  const send = async () => {
    if (!fragment.track) return;
    setBusy(true);
    setError("");
    try {
      await postJson("/api/fragments", {
        trackId: fragment.track.spotifyId,
        parentFragmentId: fragment.id,
        destination: mode === "profile"
          ? { kind: visibility === "public" ? "profile_wall" : "profile_inbox", id: handle }
          : { kind: "room_queue", id: roomId },
        visibility: mode === "room" ? "public" : visibility,
        sourceKind: "fragment",
        note: note.trim() || null,
      });
      onChanged();
      onClose();
    } catch (cause: any) {
      setError(cause?.message || "Could not pass this Fragment along");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fragment-action-panel">
      <div className="segmented" role="group" aria-label="Fragment destination">
        <button className={mode === "profile" ? "active" : ""} onClick={() => setMode("profile")}>Profile</button>
        <button className={mode === "room" ? "active" : ""} onClick={() => setMode("room")}>Room</button>
      </div>
      {mode === "profile" ? (
        <>
          <label><span>Exact handle</span><input value={handle} onChange={(event) => setHandle(event.target.value)} placeholder="@sean-4f7a91" /></label>
          <div className="segmented visibility-toggle" role="group" aria-label="Recommendation visibility">
            <button className={visibility === "public" ? "active" : ""} onClick={() => setVisibility("public")}>Public Wall</button>
            <button className={visibility === "private" ? "active" : ""} onClick={() => setVisibility("private")}>Private Inbox</button>
          </div>
        </>
      ) : (
        <label><span>Room</span><select value={roomId} onChange={(event) => setRoomId(event.target.value)}>
          {rooms.map((room) => <option key={room.id} value={room.id}>{room.title}</option>)}
        </select></label>
      )}
      <label><span>Note <small>{note.length}/280</small></span><textarea maxLength={280} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Why this one?" /></label>
      {error && <p className="field-error" role="status">{error}</p>}
      <div className="panel-actions">
        <button className="button ghost" onClick={onClose}>Cancel</button>
        <button className="button primary" disabled={busy || (mode === "profile" ? !handle.trim() : !roomId)} onClick={send}>{busy ? "Sending…" : "Send Fragment"}</button>
      </div>
    </div>
  );
}

function Trail({ fragmentId, onClose }: { fragmentId: string; onClose: () => void }) {
  const [trail, setTrail] = React.useState<{ ancestry: FragmentView[]; nextHops: FragmentView[] } | null>(null);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    void api<{ trail: { ancestry: FragmentView[]; nextHops: FragmentView[] } }>(`/api/fragments/${encodeURIComponent(fragmentId)}/trail`)
      .then((payload) => setTrail(payload.trail))
      .catch((cause) => setError(cause?.message || "Trail unavailable"));
  }, [fragmentId]);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="trail-dialog" role="dialog" aria-modal="true" aria-labelledby="trail-title">
        <header><div><span className="eyebrow">Fragment lineage</span><h2 id="trail-title">How this song traveled</h2></div><button className="icon-button" onClick={onClose} aria-label="Close trail">×</button></header>
        {error && <p className="field-error">{error}</p>}
        {!trail && !error && <div className="loading-line">Tracing the path…</div>}
        {trail && (
          <div className="trail-map">
            {trail.ancestry.map((node, index) => (
              <div className={`trail-node ${node.masked ? "masked" : ""}`} style={{ "--trail-index": index } as React.CSSProperties} key={node.id}>
                <span className="trail-step">{index + 1}</span>
                <div>
                  <strong>{node.masked ? "Private recommendation" : node.track?.title}</strong>
                  <small>{node.masked ? "Identity and note hidden" : <>from <AppLink href={`/u/${node.creator?.handle}`}>@{node.creator?.handle}</AppLink> · {node.sourceKind}</>}</small>
                  {!node.masked && node.note && <p>“{node.note}”</p>}
                </div>
              </div>
            ))}
            {trail.nextHops.length > 0 && <div className="next-hop-label">Passed along next</div>}
            {trail.nextHops.map((node, index) => (
              <div className="trail-node next-hop" style={{ "--trail-index": trail.ancestry.length + index } as React.CSSProperties} key={node.id}>
                <span className="trail-step">↗</span>
                <div><strong>{node.track?.title}</strong><small>via @{node.creator?.handle}</small></div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function FragmentCard({ fragment, authenticated, currentHandle, onRequireAuth, onChanged, ownerActions = false }: {
  fragment: FragmentView;
  authenticated: boolean;
  currentHandle?: string | null;
  onRequireAuth: (pendingAction?: string) => void;
  onChanged: () => void;
  ownerActions?: boolean;
}) {
  const { playFragment } = useRoomSession();
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [panelMode, setPanelMode] = React.useState<"profile" | "room">("profile");
  const [trailOpen, setTrailOpen] = React.useState(false);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState("");
  if (fragment.masked) {
    return <article className="fragment-card masked-fragment"><span className="privacy-lock">●</span><div><strong>Private recommendation</strong><p>Part of this trail is hidden.</p></div></article>;
  }

  const action = async (name: "save" | "hide" | "dismiss" | "retract") => {
    if (!authenticated) return onRequireAuth(`${name}:${fragment.id}`);
    setBusy(name);
    setError("");
    try {
      await postJson(`/api/fragments/${fragment.id}/actions`, { action: name }, idempotencyKey());
      onChanged();
    } catch (cause: any) {
      setError(cause?.message || "Fragment action failed");
    } finally {
      setBusy(null);
    }
  };

  const created = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(fragment.createdAt));
  return (
    <article className="fragment-card">
      <div className="fragment-main">
        <div className="track-art">
          {fragment.track?.artworkUrl ? <img src={fragment.track.artworkUrl} width="96" height="96" loading="lazy" alt="" /> : <span>♪</span>}
        </div>
        <div className="fragment-copy">
          <div className="fragment-meta">
            <span className={`visibility-badge ${fragment.sourceKind === "spotify" ? "featured" : fragment.visibility}`}>{fragment.sourceKind === "spotify" ? `Featured${fragment.featuredRank ? ` #${fragment.featuredRank}` : ""}` : fragment.visibility}</span>
            <span>{created}</span>
            {fragment.propagationCount > 0 && <span>{fragment.propagationCount} pass{fragment.propagationCount === 1 ? "" : "es"}</span>}
          </div>
          <h3>{fragment.track?.spotifyUrl ? <a href={fragment.track.spotifyUrl} target="_blank" rel="noreferrer">{fragment.track.title}</a> : fragment.track?.title}</h3>
          <p className="track-byline">{fragment.track?.artist}{fragment.track?.album ? ` · ${fragment.track.album}` : ""}</p>
          {fragment.creator && <p className="fragment-attribution">{fragment.sourceKind === "spotify" ? "featured by " : "sent by "}<AppLink href={`/u/${fragment.creator.handle}`}>@{fragment.creator.handle}</AppLink>{fragment.sourceKind !== "spotify" && fragment.recipient ? <> to <AppLink href={`/u/${fragment.recipient.handle}`}>@{fragment.recipient.handle}</AppLink></> : null}{fragment.track?.spotifyUrl && <> · <a className="spotify-track-link" href={fragment.track.spotifyUrl} target="_blank" rel="noreferrer">● Open in Spotify ↗</a></>}</p>}
          {fragment.note && <blockquote>{fragment.note}</blockquote>}
          {fragment.addedToLibrary && <span className="saved-stamp">Added to Library</span>}
        </div>
      </div>
      <div className="fragment-actions">
        <button onClick={() => authenticated ? void playFragment(fragment.id).catch((cause) => setError(cause.message)) : onRequireAuth(`play:${fragment.id}`)}>▶ <span>Play Now</span></button>
        <button onClick={() => {
          if (!authenticated) return onRequireAuth(`send-profile:${fragment.id}`);
          setPanelMode("profile");
          setPanelOpen(true);
        }}>↗ <span>Send to Profile</span></button>
        <button onClick={() => {
          if (!authenticated) return onRequireAuth(`send-room:${fragment.id}`);
          setPanelMode("room");
          setPanelOpen(true);
        }}>⌂ <span>Send to Room</span></button>
        <button disabled={busy === "save" || fragment.addedToLibrary} onClick={() => void action("save")}>＋ <span>{fragment.addedToLibrary ? "In Library" : "Add to Library"}</span></button>
        <button onClick={() => setTrailOpen(true)}>⌁ <span>View Trail</span></button>
        {ownerActions && fragment.visibility === "public" && <button onClick={() => void action("hide")}>◌ <span>Hide</span></button>}
        {ownerActions && fragment.visibility === "private" && <button onClick={() => void action("dismiss")}>× <span>Dismiss</span></button>}
        {authenticated && currentHandle === fragment.creator?.handle && <button onClick={() => void action("retract")}>↩ <span>Retract</span></button>}
      </div>
      {error && <p className="field-error fragment-error" role="status">{error}</p>}
      {panelOpen && <ActionPanel key={panelMode} fragment={fragment} initialMode={panelMode} onClose={() => setPanelOpen(false)} onChanged={onChanged} />}
      {trailOpen && <Trail fragmentId={fragment.id} onClose={() => setTrailOpen(false)} />}
    </article>
  );
}
